import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addDays } from 'date-fns'
import { Formik } from 'formik'
import { cloneDeep, find } from 'lodash'
import * as Yup from 'yup'

import {
  DateTimePicker,
  MultiSelect,
  Textarea,
  Button,
  Checkbox,
  Select,
  SelectScheduleType,
  WagePremiums,
  BillingDetails,
  Attributes,
  FormRow,
  Form
} from 'components/Forms'
import BaseModal from 'components/Modals/BaseModal'
import { Breaks, IsShiftOver16Hours, ShiftMultiCreation, UserInput, AutoRecalculateBreaks } from 'components/Schedule'
import { Collapsable } from 'components/UI'
import { Parse } from 'services/Dates'
import { parseToTemplate, somewhatRandomID } from 'services/ScheduleTemplates'
import { getAssignablePositions } from 'services/Structure'
import { getProjectsForMultiselect } from 'services/getProjectsForMultiselect'
import { getParsedTagsForMultiselect } from 'services/getParsedTagsForMultiselect'
import { closeModal } from 'store/actions/modals'
import { hasAccessToThis } from 'services/Utilities'
import { updateEvents } from 'store/actions/scheduleTemplates'
import { getBreaksToApply, getScheduleTypeInitialvalue } from 'services/schedule'
import { Alert } from 'ui'

import { t } from 'translations'

const ScheduleTemplateEventEditModal = ({ creating, end, existingEvent, id, multiDayIndex, positionId, start, scheduleTemplateId }) => {
  const dispatch = useDispatch()
  const { _id: userId } = useSelector(state => state.users.userInfo)
  const { projects, scheduleTemplatesById } = useSelector(state => state.resources)
  const { enableBillingDetails, projectIsRequired, breaksAreRequired } = useSelector(state => state.settings)
  const { events, days: numberOfDaysInTemplate, requiredPositions } = useSelector(state => state.scheduleTemplates)

  const { enableShiftSpecificAttributes, ignoreReportsToForProjects } = useSelector(state => state.settings)

  const event = existingEvent ? find(events, { id }) : { id, start, end, breaks: getBreaksToApply({ start, end }, positionId), multiDayIndex, positionId }

  const showOtherInfo = projectIsRequired || breaksAreRequired

  const onSubmit = async ({ addToOtherDays = [], numberOfIdentical, ...values }) => {
    // Parse project field that is not an array in frontend
    values.projects = values.projects && values.projects.length ? [values.projects] : []

    let eventsToUpdate = [values]

    // When creating, options to add on multiple days and add identical shifts are active.
    if (creating) {
      // Create on multiple days
      for (const multiDayIndex of addToOtherDays) {
        eventsToUpdate.push({ ...cloneDeep(values), multiDayIndex, id: somewhatRandomID() })
      }
      if (numberOfIdentical > 1) {
        // Clone original array to avoid exponentially adding more.
        const originalEventsToUpdate = cloneDeep(eventsToUpdate)
        // Add a clone of the array the number of times required by the user
        for (let index = 1; index < numberOfIdentical; index++) {
          eventsToUpdate.push(...originalEventsToUpdate.map(event => cloneDeep({ ...event, id: somewhatRandomID() })))
        }
      }
    }

    dispatch(updateEvents(eventsToUpdate.map(parseToTemplate)))
    dispatch(closeModal())
  }

  const breaksShape = Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      duration: Yup.number(),
      paid: Yup.boolean().nullable()
    })
  )

  const shiftShape = {
    _id: Yup.string(),
    employeeId: Yup.string().nullable(),
    positionId: Yup.string().required(),
    projects: Yup.string().nullable(),
    start: Yup.mixed().required(),
    end: Yup.mixed()
      .test('is-greater', t('components.END_TIME_BEFORE_START_TIME'), function (value) {
        const { start } = this.parent
        let startDate = Parse(start, { inFormat: 'serverTime' })
        let endDate = Parse(value, { inFormat: 'serverTime' })
        if (startDate >= endDate) endDate = addDays(endDate, 1)
        return startDate < endDate
      })
      .required(),
    comment: Yup.string(),
    isExtraShift: Yup.boolean().nullable(),
    isOvertime: Yup.boolean().nullable(),
    isOnCall: Yup.boolean().nullable(),
    isIrreplaceable: Yup.boolean().nullable(),
    needsReplacement: Yup.boolean().nullable(),
    noEmployeeAvailable: Yup.boolean().nullable(),
    hasSpecificRequirements: Yup.boolean().nullable(),
    coveredOtherUnit: Yup.boolean().nullable(),
    tags: Yup.array(Yup.string()),
    changeReason: Yup.string(),
    changeDescription: Yup.string(),
    numberOfIdentical: Yup.number().min(0).max(100, 'manager.schedule.TOO_MUCH_IDENTICAL_SHIFT').integer(),
    breaks: breaksAreRequired ? breaksShape.min(1, t('manager.schedule.REQUIRED_FIELD')) : breaksShape
  }

  return (
    <BaseModal hideButtons title={`manager.schedule.${creating ? 'CREATE_MODAL' : 'EDIT_MODAL'}`}>
      <Formik
        enableReinitialize={true}
        validationSchema={Yup.object().shape(shiftShape)}
        initialValues={{
          employeeId: '',
          positionId: '',
          comment: '',
          isExtraShift: false,
          isOvertime: false,
          isOnCall: false,
          isIrreplaceable: false,
          needsReplacement: false,
          coveredOtherUnit: false,
          noEmployeeAvailable: false,
          hasSpecificRequirements: false,
          tags: scheduleTemplatesById[scheduleTemplateId]?.defaultShiftTags || [],
          breaks: [],
          wagePremiums: [],
          requiredAttributes: [],
          numberOfIdentical: 1,
          addToOtherDays: [],
          selectedScheduleType: getScheduleTypeInitialvalue([event]),
          billingDetails: { billingId: '' },
          ...event,
          projects: Array.isArray(event.projects) ? event.projects[0] : undefined
        }}
        onSubmit={onSubmit}
      >
        {({ values, initialValues, status, isSubmitting, setStatus, setFieldValue }) => {
          return (
            <Form>
              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <IsShiftOver16Hours {...values} />

              {/* Form */}
              {/* Main info */}
              <UserInput />

              <MultiSelect
                module="manager.schedule.eventForm"
                name="positionId"
                required
                options={getAssignablePositions({ forMultiSelect: true, filterByIds: requiredPositions })}
              />
              <FormRow>
                <DateTimePicker module="manager.schedule.eventForm" name="start" required type="time" />
                <DateTimePicker module="manager.schedule.eventForm" name="end" required type="time" />
              </FormRow>

              <AutoRecalculateBreaks values={values} initialValues={initialValues} setFieldValue={setFieldValue} />

              {/* Radios for selecting shift type */}
              <SelectScheduleType shift={values} setFieldValue={setFieldValue} isTemplate />

              {/* This option is only shown in multi-day templates */}
              {numberOfDaysInTemplate > 1 ? (
                <Select
                  module="manager.scheduleTemplates.form"
                  name="multiDayIndex"
                  options={new Array(numberOfDaysInTemplate)
                    .fill(1)
                    .map((option, index) => ({ _id: index, name: t('manager.scheduleTemplates.form.MULTIDAYINDEX.OPTION', { day: index + 1 }) }))}
                />
              ) : null}

              <Textarea module="manager.schedule.eventForm" name="comment" isTranslatable />

              {/* Multi-creation */}
              <ShiftMultiCreation isCreating={creating} amountOfDay={numberOfDaysInTemplate} isTemplate multiDayIndex={parseInt(values.multiDayIndex)} />

              {/* Shift options */}
              <Collapsable type="shiftOptions" title="manager.schedule.eventForm.SHIFT_OPTIONS">
                <Checkbox module="manager.schedule.eventForm" name="isExtraShift" />
                <Checkbox module="manager.schedule.eventForm" name="isOvertime" />
                <Checkbox module="manager.schedule.eventForm" name="isOnCall" />
                <Checkbox module="manager.schedule.eventForm" name="isIrreplaceable" />
                <Checkbox module="manager.schedule.eventForm" name="needsReplacement" />
                <Checkbox module="manager.schedule.eventForm" name="noEmployeeAvailable" />
                <Checkbox module="manager.schedule.eventForm" name="hasSpecificRequirements" />
                <Checkbox module="manager.schedule.eventForm" name="coveredOtherUnit" />
              </Collapsable>

              {/* Extra info */}
              <Collapsable type="otherInfo" title="manager.schedule.eventForm.OTHER_INFO" show={showOtherInfo}>
                {!scheduleTemplatesById[scheduleTemplateId]?.projects?.length ? (
                  <MultiSelect
                    module="manager.schedule.eventForm"
                    name="projects"
                    options={getProjectsForMultiselect(projects, {
                      ignoreReportsToForProjects,
                      permission: !!hasAccessToThis('limitToReportsTo'),
                      connectedUser: userId,
                      t
                    })}
                    isClearable
                    required={projectIsRequired}
                    isOptionDisabled={({ isDisabled }) => isDisabled}
                  />
                ) : null}
                <MultiSelect module="manager.schedule.eventForm" name="tags" options={getParsedTagsForMultiselect()} isClearable isMulti />
                <Breaks
                  event={{ start: values.start, end: values.end, positionId: values.positionId }}
                  setFieldValue={(...args) => setFieldValue(...args)}
                  breaks={values.breaks}
                  allowRecalculate
                  required={breaksAreRequired}
                />

                <WagePremiums wagePremiums={values.wagePremiums} />
                {enableShiftSpecificAttributes ? (
                  <Attributes module="manager.schedule.eventForm" name="requiredAttributes" attributes={values.requiredAttributes} />
                ) : null}
              </Collapsable>
              {!!enableBillingDetails && (
                <Collapsable type="billingDetails" title="components.billingDetails.BILLING_DETAILS">
                  <BillingDetails context="shift" />
                </Collapsable>
              )}

              <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
              <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                {t(`actions.CLOSE`)}
              </Button>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ScheduleTemplateEventEditModal
